import type {StaticImageData} from 'next/image';
import {cdnImage} from '@/util/cdnImage';

// ─── Section illustrations & icons ───────────────────────────────────────────

// Page-specific assets live in the gen-ai-integrations folder on the image CDN.
const genAiAsset = (file: string): StaticImageData => cdnImage(`gen-ai-integrations/${file}`);

/** Business challenges (ChallengeGrid) — one icon per card. */
const chDisconnectedKnowledge = genAiAsset('disconnected-business-knowledge.svg');
const chManualWork = genAiAsset('high-volume-manual-work.svg');
const chStandalonePilots = genAiAsset('standalone-ai-pilots.svg');
const chCustomerService = genAiAsset('inconsistent-customer-service.svg');
const chDataPrivacy = genAiAsset('data-privacy-and-governance-risk.svg');
const chOperatingCosts = genAiAsset('uncontrolled-ai-operating-costs.svg');

/** KeyServices — one icon per service, in the same order as the cards below. */
const svcStrategy = genAiAsset('generative-ai-strategy-and-consulting.svg');
const svcLlm = genAiAsset('llm-and-slm-integration-services.svg');
const svcRag = genAiAsset('retrieval-augmented-generation-rag-development.svg');
const svcCopilot = genAiAsset('ai-copilot-development.svg');
const svcAgents = genAiAsset('ai-agent-and-agentic-workflow-integration.svg');
const svcAppIntegration = genAiAsset('enterprise-application-integration.svg');
const svcWorkflow = genAiAsset('ai-workflow-automation.svg');
const svcGovernance = genAiAsset('ai-governance-security-and-compliance.svg');
const svcLlmops = genAiAsset('llmops-and-continuous-optimization.svg');

/** Enterprise security & governance controls (HomeBusiness). */
const ctrlIdentity = genAiAsset('identity-and-access-management.svg');
const ctrlDataProtection = genAiAsset('data-protection.svg');
const ctrlPromptSecurity = genAiAsset('prompt-and-input-security.svg');
const ctrlOutputValidation = genAiAsset('output-validation.svg');
const ctrlAuditability = genAiAsset('auditability-and-monitoring.svg');
const ctrlPrivateDeployment = genAiAsset('private-and-hybrid-deployment.svg');
const ctrlCompliance = genAiAsset('compliance-alignment.svg');
const ctrlResponsibleAi = genAiAsset('responsible-ai-governance.svg');

/** Compliance framework badges (ChatbotIntegrations platform grid). */
const compGdpr = genAiAsset('gdpr-compliance.webp');
const compHipaa = genAiAsset('hipaa-compliance.webp');
const compSoc2 = genAiAsset('soc2-compliance.webp');
const compIso27001 = genAiAsset('iso-27001-compliance.webp');
const compPciDss = genAiAsset('pci-dss-compliance.webp');
const compCcpa = genAiAsset('ccpa-compliance.webp');
const compNistAiRmf = genAiAsset('nist-ai-rmfcompliance.webp');
const compEuAiAct = genAiAsset('eu-ai-act-readiness-compliance.webp');

/**
 * Industry solution icons. Only "Enterprise Software & SaaS" ships in this page's
 * folder; the rest reuse the shared industry icon set already on the CDN.
 */
const indFinancial = cdnImage('custom-software-developement-new/financial-services-fintech-icon.webp');
const indHealthcare = cdnImage('custom-software-developement-new/healthcare-life-sciences-icon.webp');
const indLogistics = cdnImage('custom-software-developement-new/logistics-supply-chain-icon.webp');
const indRetail = cdnImage('custom-software-developement-new/retail-ecommerce-icon.webp');
const indManufacturing = cdnImage('custom-software-developement-new/manufacturing-industrial-icon.webp');
const indEnterpriseSaas = genAiAsset('enterprise-software-and-saas.svg');
const indRealEstate = cdnImage('custom-software-developement-new/real-estate-proptech-icon.webp');
const indInsurance = cdnImage('custom-software-developement-new/insurance-insurtech-icon.webp');

/** Continuous optimization steps (HomeServices) — served from the CDN. */
const optResponseQuality = genAiAsset('response-quality-monitoring.svg');
const optModelEvaluation = genAiAsset('model-evaluation-and-upgrades.svg');
const optCostOptimization = genAiAsset('token-and-api-cost-optimization.svg');
const optRetrievalPerformance = genAiAsset('retrieval-performance-improvement.svg');
const optPromptVersioning = genAiAsset('prompt-and-workflow-versioning.svg');
const optSecurityReviews = genAiAsset('security-and-governance-reviews.svg');

/** Business outcomes (CoreServices, navy tiles). */
const valKnowledgeAccess = genAiAsset('faster-knowledge-access.svg');
const valManualWork = genAiAsset('reduced-manual-work.svg');
const valCustomerResponse = genAiAsset('improved-customer-response.svg');
const valConsistentOps = genAiAsset('more-consistent-operations.svg');
const valProductivity = genAiAsset('stronger-employee-productivity.svg');
const valControlledAdoption = genAiAsset('controlled-ai-adoption.svg');
const valInnovation = genAiAsset('faster-product-innovation.svg');
const valScalableOps = genAiAsset('scalable-ai-operations.svg');

/** Why Debut Infotech (WhatSetsBest). */
const whyBusinessFirst = genAiAsset('business-first-integration-strategy.svg');
const whyEnterpriseScale = genAiAsset('enterprise-scale-engineering-experience.svg');
const whyEndToEnd = genAiAsset('end-to-end-generative-ai-integration-services.svg');
const whySeamlessIntegration = genAiAsset('seamless-integration-across-enterprise-systems.svg');

/** Section illustrations. */
export const genAiServicesImage = genAiAsset('generative-ai-integration-services-built-around-business-operations.webp');
export const genAiArchitectureImage = genAiAsset('generative-ai-integration-architecture-diagram.webp');
export const genAiScaleCtaImage = genAiAsset('scale-generative-ai-with-greater-control-clarity.webp');
export const genAiReadyCtaImage = genAiAsset('ready-to-integrate-generative-ai.webp');

const ecoOpenAI = cdnImage('ai-chatbot-development-new/openai-gpt-chatgpt-platform.webp');
const ecoAnthropic = cdnImage('ai-chatbot-development-new/anthropic-claude-platform.webp');
const ecoGoogle = cdnImage('ai-chatbot-development-new/google-gemini-platform.webp');
const ecoAzureOpenAI = cdnImage('ai-copliot-development-new/Azure-OpenAI-Service-partner-logo.webp');
const ecoAws = cdnImage('ai-chatbot-development-new/amazon-bedrock-platform.webp');
const ecoNvidia = cdnImage('ai/NVIDIA.webp');
const ecoHuggingFace = cdnImage('ai-chatbot-development-new/huggingface-platform.webp');
const ecoMeta = cdnImage('ai-chatbot-development-new/meta-llama-platform.webp');
const ecoMistral = cdnImage('ai-chatbot-development-new/mistral-ai-platform.webp');

export const genAiBannerBottom = [
    {src: ecoOpenAI, alt: 'OpenAI'},
    {src: ecoAnthropic, alt: 'Anthropic'},
    {src: ecoGoogle, alt: 'Google Cloud'},
    {src: ecoAzureOpenAI, alt: 'Azure OpenAI Service'},
    {src: ecoAws, alt: 'AWS'},
    {src: ecoNvidia, alt: 'NVIDIA'},
    {src: ecoHuggingFace, alt: 'Hugging Face'},
    {src: ecoMeta, alt: 'Meta Llama'},
    {src: ecoMistral, alt: 'Mistral'}
];

// ─── Business challenges ─────────────────────────────────────────────────────

export const genAiChallengesData = {
    heading: 'What Business Challenges Can Generative AI Integration Solve?',
    subheading:
        'Many enterprise AI initiatives prove technical feasibility but fail to improve daily operations. The gap is usually trusted data access, workflow integration, governance, and a clear operating model. Our <strong>generative AI integration services</strong> address these barriers and turn isolated pilots into repeatable business value.',
    cards: [
        {
            src: chDisconnectedKnowledge,
            title: 'Disconnected Business Knowledge',
            details: [
                {
                    label: 'Problem',
                    text: 'Employees lose time searching across documents, shared drives, CRM records, policies, and internal portals.'
                },
                {
                    label: 'Business Impact',
                    text: 'Productivity declines, decision-making becomes inconsistent, and customer responses take longer.'
                },
                {
                    label: 'Solution',
                    text: 'We connect approved knowledge sources to AI assistants that deliver relevant, traceable, and permission-aware responses.'
                }
            ]
        },
        {
            src: chManualWork,
            title: 'High-Volume Manual Work',
            details: [
                {
                    label: 'Problem',
                    text: 'Teams repeatedly summarize documents, draft responses, classify requests, and transfer information between systems.'
                },
                {
                    label: 'Business Impact',
                    text: 'Manual effort increases operational costs, slows execution, and reduces workforce productivity.'
                },
                {
                    label: 'Solution',
                    text: 'We integrate generative AI into these workflows to automate repetitive tasks while maintaining appropriate human oversight.'
                }
            ]
        },
        {
            src: chStandalonePilots,
            title: 'Standalone AI Pilots',
            details: [
                {label: 'Problem', text: 'Many AI initiatives remain isolated from production systems after the proof-of-concept stage.'},
                {
                    label: 'Business Impact',
                    text: 'Business value is delayed, duplicate effort increases, and enterprise-wide adoption becomes difficult.'
                },
                {
                    label: 'Solution',
                    text: 'We transform validated pilots into secure integrations connected to enterprise applications, data platforms, APIs, and business workflows.'
                }
            ]
        },
        {
            src: chCustomerService,
            title: 'Inconsistent Customer Service',
            details: [
                {label: 'Problem', text: 'Support teams rely on fragmented information and manual searches to resolve customer requests.'},
                {label: 'Business Impact', text: 'Response times increase, service quality varies, and customer satisfaction declines.'},
                {
                    label: 'Solution',
                    text: 'We build AI assistants connected to customer history, product data, knowledge bases, and escalation workflows.'
                }
            ]
        },
        {
            src: chDataPrivacy,
            title: 'Data Privacy and Governance Risk',
            details: [
                {
                    label: 'Problem',
                    text: 'Public AI models used without appropriate controls can expose sensitive business information and create compliance risks.'
                },
                {
                    label: 'Business Impact',
                    text: 'Organizations face increased security exposure, regulatory challenges, and governance complexity.'
                },
                {
                    label: 'Solution',
                    text: "We implement secure <a href='/blog/generative-ai-architecture' target='_blank'>generative AI architectures</a> with role-based access, audit trails, data protection, and policy enforcement."
                }
            ]
        },
        {
            src: chOperatingCosts,
            title: 'Uncontrolled AI Operating Costs',
            details: [
                {
                    label: 'Problem',
                    text: 'Poor model selection, inefficient prompts, and unmanaged token consumption increase AI operating expenses.'
                },
                {
                    label: 'Business Impact',
                    text: "Costs grow over time, reducing ROI and making <a href='/ai-deployment-services' target='_blank'>enterprise AI deployments</a> difficult to scale."
                },
                {
                    label: 'Solution',
                    text: 'We optimize model routing, prompt design, caching, and monitoring to improve efficiency while controlling long-term operating costs.'
                }
            ]
        }
    ]
};

// ─── Our services (KeyServices) ──────────────────────────────────────────────

export const genAiKeyServicesData = [
    {
        icon: svcStrategy,
        title: 'Generative AI Strategy & Consulting',
        description:
            "We assess business readiness, identify high-value use cases, define the target architecture, evaluate build-versus-buy options, and develop a phased implementation roadmap that aligns AI initiatives with business objectives, governance requirements, and expected return on investment. Where appropriate, our approach incorporates <a href='/machine-learning-development-company' target='_blank'>machine learning development</a> and ML strategy to support broader enterprise AI initiatives."
    },
    {
        icon: svcLlm,
        title: 'LLM & SLM Integration Services',
        description:
            'We integrate large and small language models with enterprise applications, customer-facing platforms, and internal business systems. Our team supports commercial, open-source, private, and hybrid deployments while selecting the most appropriate models based on accuracy, privacy, latency, scalability, and cost requirements.'
    },
    {
        icon: svcRag,
        title: 'Retrieval-Augmented Generation (RAG) Development',
        description:
            "We build Retrieval-Augmented Generation (RAG) solutions that connect <a href='/blog/a-comprehensive-guide-to-understanding-and-using-ai-models' target='_blank'>AI models</a> with enterprise documents, databases, knowledge bases, and business repositories. Our RAG architectures improve response accuracy, preserve source traceability, enforce data permissions, and keep business knowledge continuously up to date."
    },
    {
        icon: svcCopilot,
        title: 'AI Copilot Development',
        redirect: '',
        description:
            "We <a href='/ai-copilot-development' target='_blank'>develop AI copilots</a> that assist employees within the applications they already use. These copilots retrieve enterprise knowledge, generate content, summarize information, answer business questions, and support day-to-day decision-making while respecting user roles, permissions, and organizational policies."
    },
    {
        icon: svcAgents,
        title: 'AI Agent & Agentic Workflow Integration',
        redirect: '',
        description:
            "We <a href='/ai-agent-development-company' target='_blank'>develop AI agents</a> that execute defined business tasks across enterprise workflows. Agents retrieve approved information, interact with business applications, call authorized tools, automate multi-step processes, and escalate exceptions whenever human approval or judgment is required."
    },
    {
        icon: svcAppIntegration,
        title: 'Enterprise Application Integration',
        description:
            "We connect generative AI with CRM, ERP, CMS, collaboration platforms, customer support systems, APIs, <a href='/blog/generative-ai-in-saas-development' target='_blank'>SaaS applications</a>, and legacy <a href='/enterprise-software-development' target='_blank'>enterprise software</a>. This enables AI capabilities to operate within existing business processes without disrupting established systems or user experiences."
    },
    {
        icon: svcWorkflow,
        title: 'AI Workflow Automation',
        description:
            "We automate repetitive, document-intensive, and decision-support processes by combining generative AI with enterprise workflows. Where appropriate, we incorporate <a href='/blog/what-is-predictive-analytics' target='_blank'>predictive analytics</a> to improve forecasting, recommendations, and operational decision-making while reducing manual effort and increasing process efficiency."
    },
    {
        icon: svcGovernance,
        title: 'AI Governance, Security & Compliance',
        description:
            'We embed governance into every implementation through identity management, access controls, encryption, prompt-injection protection, output validation, audit logging, and compliance controls. Solutions can be aligned with organizational requirements such as GDPR, HIPAA, SOC 2, ISO 27001, and other industry regulations.'
    },
    {
        icon: svcLlmops,
        title: 'LLMOps & Continuous Optimization',
        description:
            "We continuously monitor response quality, retrieval accuracy, latency, token consumption, operational costs, and user feedback. Prompt management, model updates, performance evaluation, and production optimization ensure AI systems remain reliable, secure, and cost-efficient. Our <a href='/mlops-consulting-services' target='_blank'>MLOps consulting</a> capabilities support model deployment, monitoring, and continuous improvement across enterprise AI environments."
    }
];

// ─── CTAs ────────────────────────────────────────────────────────────────────

export const genAiReadinessData = {
    heading: 'Is Your Business Ready for Enterprise Generative AI Integration?',
    subheading:
        'Generative AI is most likely to deliver value when it addresses a defined workflow, uses reliable business data and has measurable success criteria. The following conditions typically indicate that an organization is ready to move from exploration to integration.',
    items: [
        'Your teams spend significant time searching, summarizing, drafting, classifying, or transferring information.',
        'Your business knowledge is distributed across CRM, ERP, documents, portals, databases, and collaboration platforms.',
        'You have an AI proof of concept that must be connected to production systems.',
        'You need a secure internal copilot, knowledge assistant, or customer support assistant.',
        'You want AI agents to complete controlled actions across existing tools.',
        'You need stronger governance, cost visibility, and quality monitoring for current AI usage.'
    ],
    buttonText: 'Assess Your AI Readiness'
};

export const genAiArchitectureData = {
    cards: [
        {
            title: 'Built for Enterprise Operations',
            desc: 'The architecture is designed around four production requirements.'
        },
        {
            title: 'Connected Business Context',
            desc: 'AI securely accesses approved enterprise knowledge, applications, and operational data.'
        },
        {
            title: 'Controlled Model Execution',
            desc: 'Orchestration services manage model selection, prompts, agents, tools, and human approvals.'
        },
        {
            title: 'Embedded Security and Governance',
            desc: 'Identity, permissions, privacy controls, auditability, and compliance operate across every layer.'
        },
        {
            title: 'Continuous Operational Control',
            desc: 'Evaluation and LLMOps monitor quality, latency, reliability, usage, and operating cost after deployment.'
        }
    ]
};

export const genAiCTA1Data = {
    heading: 'Scale Generative AI With Greater Control, Clarity, and Confidence',
    subheading:
        'Define the business case, architecture, delivery model, and governance controls needed for secure enterprise-wide adoption.',
    buttonText: 'Speak With Our AI Integration Team'
};

export const genAiCTA2Data = {
    heading: 'Ready to Integrate Generative AI Into Your Enterprise Systems?',
    subheading:
        'Share your current applications, data sources, workflow challenges, and security requirements. Our team will help you define a practical integration scope, architecture, delivery plan, and next step.',
    buttonText: 'Contact Our Experts Now!'
};

// ─── Industries we serve (IndustrySolutions) ─────────────────────────────────

export const genAiIndustrySolutionsData = {
    heading: 'How Generative AI Integration Adapts to Different Industries',
    subheading:
        'Generative AI delivers the greatest business value when it aligns with industry-specific workflows, regulatory requirements, and operational priorities. We design integration architectures, data access controls, governance frameworks, and AI workflows that reflect the way each industry operates.',
    cards: [
        {
            icon: indFinancial,
            category: 'Financial Services',
            heading: 'Secure, Compliance-Aligned AI for Financial Operations',
            description:
                'Improve operational efficiency, strengthen compliance, and enhance customer experiences with secure, enterprise-ready AI integration.',
            tags: ['KYC / AML', 'Regulatory Compliance', 'CRM-Connected Copilots'],
            bullets: [
                'Compliance and regulatory knowledge assistants',
                'KYC, AML, and customer onboarding automation',
                'Customer service copilots connected to CRM and banking systems',
                'Financial report, policy, and correspondence generation'
            ]
        },
        {
            icon: indHealthcare,
            category: 'Healthcare',
            heading: 'HIPAA-Aligned Clinical and Administrative AI',
            description: 'Improve clinical efficiency, reduce administrative burden, and enable faster, data-driven patient care.',
            tags: ['HIPAA', 'Clinical Documentation', 'Patient Triage'],
            bullets: [
                'Clinical documentation assistants (SOAP notes, discharge summaries)',
                'Patient inquiry and triage copilots',
                'Prior authorization and insurance query automation',
                'HIPAA-compliant knowledge retrieval for clinical staff'
            ]
        },
        {
            icon: indLogistics,
            category: 'Logistics & Supply Chain',
            heading: 'AI Across Shipment, Vendor, and Transport Workflows',
            description: 'Increase operational visibility, reduce manual coordination, and improve supply chain responsiveness.',
            tags: ['ERP / TMS / WMS', 'Exception Management', 'Customs Documentation'],
            bullets: [
                'Shipment tracking and exception management assistants',
                'Vendor and customer communication automation',
                'ERP, TMS, and WMS-connected operations copilots',
                'Customs documentation and transport workflow automation'
            ]
        },
        {
            icon: indRetail,
            category: 'Retail & eCommerce',
            heading: 'Personalized Commerce and Merchandising Automation',
            description: 'Deliver personalized shopping experiences while improving merchandising and customer support operations.',
            tags: ['Product Discovery', 'Catalog Generation', 'Order-Aware Support'],
            bullets: [
                'AI-powered shopping and product discovery assistants',
                'Product catalog and content generation workflows',
                'Order-aware customer support automation',
                'Personalized merchandising and recommendation engines'
            ]
        },
        {
            icon: indManufacturing,
            category: 'Manufacturing',
            heading: 'Operational Knowledge and Maintenance Copilots',
            description: 'Improve production efficiency, reduce equipment downtime, and provide instant access to operational knowledge.',
            tags: ['Maintenance', 'Technical Documentation', 'Supplier Collaboration'],
            bullets: [
                'Maintenance and troubleshooting copilots',
                'Technical documentation search and summarization',
                'Production, quality, and safety knowledge assistants',
                'Supplier collaboration and operations workflow automation'
            ]
        },
        {
            icon: indEnterpriseSaas,
            category: 'Enterprise Software & SaaS',
            heading: 'Embedded AI Capabilities Inside Your Product',
            description: 'Embed AI capabilities into enterprise applications to improve productivity and user experience.',
            tags: ['Embedded Copilots', 'Natural Language Workflows', 'AI Search'],
            bullets: [
                'Embedded AI copilots and intelligent assistants',
                'Natural language workflows within enterprise software',
                'AI-powered search across customer and product data',
                'Agent-driven task automation and workflow execution'
            ]
        },
        {
            icon: indRealEstate,
            category: 'Real Estate',
            heading: 'Document-Heavy Property Operations, Automated',
            description: 'Accelerate property operations, simplify document management, and improve customer engagement.',
            tags: ['Listing Automation', 'Contract Review', 'CRM Assistance'],
            bullets: [
                'Property listing and document automation',
                'Lead qualification and CRM assistance',
                'Contract review and due diligence summarization',
                'AI-powered search across property knowledge repositories'
            ]
        },
        {
            icon: indInsurance,
            category: 'Insurance',
            heading: 'Faster Claims, Underwriting, and Policy Support',
            description:
                'Accelerate policy operations, improve claims processing, and enhance customer service with intelligent automation.',
            tags: ['Claims Review', 'Underwriting', 'Fraud Investigation'],
            bullets: [
                'Claims review and documentation assistants',
                'Policy and underwriting knowledge copilots',
                'Customer inquiry and policy support automation',
                'Fraud investigation and compliance workflow assistance'
            ]
        }
    ]
};

// ─── Cost breakdown ──────────────────────────────────────────────────────────

export const genAiCostBreakdownData = {
    tableHeaders: {col1: 'Cost Factor', col2: 'What Drives It'},
    tableRows: [
        {
            projectType: 'Systems & Integration Points',
            estimatedCost: 'Number, type, and accessibility of CRM, ERP, SaaS, APIs, data platforms, and legacy applications.'
        },
        {
            projectType: 'Data Readiness',
            estimatedCost: 'Cleansing, permissions mapping, metadata, ingestion pipelines, document processing, and content freshness.'
        },
        {
            projectType: 'RAG & Vector Search',
            estimatedCost: 'Data volume, retrieval quality, citations, reranking, access filtering, and vector database configuration.'
        },
        {
            projectType: 'AI Agent Complexity',
            estimatedCost: 'Number of tools, decision steps, approval workflows, error handling, and system actions.'
        },
        {
            projectType: 'Model & Deployment Approach',
            estimatedCost: 'Public APIs, managed cloud models, private cloud, hybrid architecture, or on-premises deployment.'
        },
        {
            projectType: 'Security & Compliance',
            estimatedCost: 'Identity controls, auditability, data residency, validation, testing, and regulatory requirements.'
        },
        {
            projectType: 'Performance & Scale',
            estimatedCost: 'User volume, concurrency, response-time targets, context size, uptime, and geographic coverage.'
        },
        {
            projectType: 'Ongoing Optimization',
            estimatedCost: 'Monitoring, evaluation, prompt management, model updates, incident support, and cost optimization.'
        }
    ]
};

// ─── Delivery framework (DevProcess) ─────────────────────────────────────────

const processChevronDown = cdnImage('web3/Web3-Process-ChevronDown.svg');
const processChevronRight = cdnImage('web3/Web3-Process-ChevronRight.svg');
const processCheckIcon = cdnImage('web3/Web3-Process-CheckIcon.svg');

export const genAiDevProcessData = {
    heading: 'Our End-to-End Generative AI Integration Process',
    subheading:
        'We follow a governed process that connects business goals, AI readiness, data preparation, enterprise integration, validation, security, deployment, and continuous improvement.',
    chevronDown: processChevronDown,
    chevronRight: processChevronRight,
    checkIcon: processCheckIcon,
    steps: [
        {
            title: 'Discovery & AI Readiness Assessment',
            phase: 'STEP 01',
            description:
                'Every successful AI initiative begins with a clear understanding of business objectives, operational workflows, user groups, and existing technology readiness. We assess where generative AI can create measurable value and whether the organization has the right data, systems, and governance foundation to support implementation.',
            checklistItems: [
                'Which workflows consume the most manual effort or repetitive decision-making?',
                'Which business teams and user groups will use the solution?',
                'Which data sources, applications, and systems need to be assessed?',
                'What security, privacy, compliance, and adoption risks must be considered?'
            ],
            tags: ['Business Discovery', 'Workflow Assessment', 'AI Readiness Review', 'Data and Technology Assessment', 'Success Metrics']
        },
        {
            title: 'Solution Architecture & Integration Planning',
            phase: 'STEP 02',
            description:
                'We define the technical architecture that connects models, enterprise data, applications, workflows, and user experiences. Architecture decisions are based on business requirements, security needs, scalability goals, and the level of control required.',
            checklistItems: [
                'Which LLMs or SLMs best fit the business requirements?',
                'Will the solution use RAG, AI agents, or hybrid architectures?',
                'Which deployment model, vector database, and integration layer should be selected?',
                'How will enterprise systems connect with AI services?'
            ],
            tags: ['Solution Architecture', 'Model Selection', 'RAG Architecture', 'Integration Design', 'Deployment Strategy']
        },
        {
            title: 'Data Preparation & Knowledge Engineering',
            phase: 'STEP 03',
            description:
                'Generative AI performs best when it can securely access trusted, structured, and up-to-date business knowledge. We prepare enterprise data for retrieval, grounding, metadata filtering, and permission-aware AI responses.',
            checklistItems: [
                'Which documents, databases, and knowledge sources should be indexed?',
                'How should content be cleaned, structured, and segmented?',
                'How will metadata, permissions, and access controls be applied?',
                'How will business information remain accurate and current?'
            ],
            tags: ['Data Ingestion', 'Knowledge Preparation', 'Metadata Design', 'Indexing Strategy', 'Access Controls']
        },
        {
            title: 'Generative AI & Enterprise System Integration',
            phase: 'STEP 04',
            description:
                'We integrate generative AI into existing enterprise applications, business processes, and operational workflows without disrupting day-to-day operations. The solution is designed to work with your systems rather than sit outside them.',
            checklistItems: [
                'Which CRM, ERP, SaaS, CMS, or legacy systems require integration?',
                'How will AI interact with enterprise APIs and business rules?',
                'Where should human approvals remain in the workflow?',
                'Which integrations require Salesforce, SAP, or custom APIs?'
            ],
            tags: ['Application Integration', 'Workflow Automation', 'API Integration', 'Enterprise Connectivity', 'Human Approval Design']
        },
        {
            title: 'Proof of Concept (PoC) & Validation',
            phase: 'STEP 05',
            description:
                'Before full-scale rollout, we validate the solution through a focused PoC or controlled pilot. This helps confirm technical feasibility, user experience, business value, and operational fit before wider implementation.',
            checklistItems: [
                'Which use case should be validated first?',
                'How does the solution perform against success metrics?',
                'Which user groups should participate in early validation?',
                'What feedback or improvements are required before rollout?'
            ],
            tags: ['PoC Development', 'Pilot Deployment', 'User Validation', 'Feedback Analysis', 'Solution Refinement']
        },
        {
            title: 'Security, Testing & Compliance',
            phase: 'STEP 06',
            description:
                'We test the solution for accuracy, security, privacy, governance, and performance before production deployment. This step ensures the AI system is reliable, controlled, and ready for enterprise use.',
            checklistItems: [
                'Are AI responses accurate, relevant, and properly grounded?',
                'Does retrieval return trusted and permission-approved information?',
                'Are security, privacy, and compliance controls working as intended?',
                'How does the solution perform under expected workloads?'
            ],
            tags: ['Quality Evaluation', 'Security Testing', 'Governance Review', 'Performance Testing', 'Compliance Validation']
        },
        {
            title: 'Production Deployment & Enterprise Rollout',
            phase: 'STEP 07',
            description:
                'After validation and testing, we deploy the solution in a controlled, phased manner. Rollout planning includes user onboarding, operational ownership, governance responsibilities, and documentation for long-term adoption.',
            checklistItems: [
                'Which business units or user groups will be onboarded first?',
                'How will users be trained and supported?',
                'Who owns ongoing governance and operations?',
                'How will rollout risks be managed?'
            ],
            tags: ['Production Deployment', 'User Training', 'Governance Framework', 'Operational Documentation', 'Change Management']
        },
        {
            title: 'Continuous Optimization & Scale',
            phase: 'STEP 08',
            description:
                'Enterprise AI requires ongoing monitoring, tuning, and improvement after deployment. We measure adoption, performance, cost, quality, and business impact while identifying opportunities to extend the solution across more workflows.',
            checklistItems: [
                'How are quality, latency, adoption, and business impact being measured?',
                'Which prompts, models, workflows, or integrations require optimization?',
                'How can operating costs and token usage be reduced?',
                'Which new business opportunities should be prioritized?'
            ],
            tags: ['Performance Monitoring', 'LLMOps', 'Prompt Optimization', 'Business Reporting', 'Continuous Improvement']
        }
    ]
};

// ─── Enterprise controls (HomeBusiness) ──────────────────────────────────────

export const genAiEnterpriseControlsData = [
    {
        src: ctrlIdentity,
        heading: 'Identity and Access Management',
        body: 'SSO, role-based access control, service authentication, least-privilege permissions, and user-level retrieval policies.'
    },
    {
        src: ctrlDataProtection,
        heading: 'Data Protection',
        body: 'Encryption, data minimization, sensitive-data masking, retention controls, tenant isolation, and secure secret management.'
    },
    {
        src: ctrlPromptSecurity,
        heading: 'Prompt and Input Security',
        body: 'Prompt-injection defenses, input filtering, tool restrictions, content boundaries, and validation before model processing.'
    },
    {
        src: ctrlOutputValidation,
        heading: 'Output Validation',
        body: 'Structured outputs, policy checks, citation requirements, confidence controls, prohibited-content checks, and human approval where needed.'
    },
    {
        src: ctrlAuditability,
        heading: 'Auditability and Monitoring',
        body: 'Prompt and response logging, access records, model and prompt version tracking, error monitoring, and reviewable decision trails.'
    },
    {
        src: ctrlPrivateDeployment,
        heading: 'Private and Hybrid Deployment',
        body: 'Private LLM, on-premises, virtual private cloud, hybrid, and controlled API architectures based on data sensitivity and infrastructure needs.'
    },
    {
        src: ctrlCompliance,
        heading: 'Compliance Alignment',
        body: "We design solutions that support compliance initiatives and can be aligned with frameworks such as GDPR, HIPAA, SOC 2, ISO 27001, PCI DSS, CCPA, NIST AI RMF, and the EU AI Act, depending on your organization's requirements and implementation scope."
    },
    {
        src: ctrlResponsibleAi,
        heading: 'Responsible AI Governance',
        body: 'Defined ownership, acceptable-use policies, model and prompt approvals, risk classification, human oversight, incident response, and periodic control reviews support accountable AI operations.'
    }
];

// ─── Compliance frameworks (ChatbotIntegrations — platform grid only) ────────

export const genAiComplianceFrameworks = [
    {label: 'GDPR', icon: compGdpr},
    {label: 'HIPAA', icon: compHipaa},
    {label: 'SOC 2', icon: compSoc2},
    {label: 'ISO 27001', icon: compIso27001},
    {label: 'PCI DSS', icon: compPciDss},
    {label: 'CCPA', icon: compCcpa},
    {label: 'NIST AI RMF', icon: compNistAiRmf},
    {label: 'EU AI Act Readiness', icon: compEuAiAct}
];

// ─── Technology ecosystem (CardTechnology) ───────────────────────────────────

const techOpenAI = cdnImage('hire-remote-developer/OpenAI-GPT.webp');
const techClaude = cdnImage('ai-copliot-development-new/anthropic.webp');
const techGemini = cdnImage('ai-development-new/Google-Gemini.webp');
const techLlama = cdnImage('hire-ai-developers/LLaMA.webp');
const techMistral = cdnImage('ai/Mistral.webp');
const techPhi = cdnImage('generative-ai-integration/GEN-ai-interation/Microsoft-Phi-2.webp');
const techCohere = cdnImage('generative-ai-integration/Cohere-API.webp');
const techHuggingFace = cdnImage('generative-ai-consulting-company/Transformers-Hugging-Face.webp');
const techCrewAI = cdnImage('Ai-agent-development/CrewAI.webp');
const techAutoGen = genAiAsset('microsoft-autogen.svg');
const techBedrock = cdnImage('generative-ai-consulting-company/Amazon-Bedrock.webp');
const techLangChain = cdnImage('generative-ai-consulting-company/LangChain.webp');
const techLlamaIndex = cdnImage('generative-ai-consulting-company/LlamaIndex.webp');
const techSemanticKernel = cdnImage('Ai-agent-development/Semantic-Kernel.webp');
const techAzure = cdnImage('generative-ai-integration/Microsoft-Azure.webp');
const techDatabricks = cdnImage('ai-engineer-tech-stack/Databricks.svg');
const techNvidia = cdnImage('home-page-new/NVIDIA.webp');
const techPinecone = cdnImage('generative-ai-consulting-company/Pinecone.webp');
const techWeaviate = cdnImage('generative-ai-consulting-company/Weaviate.webp');
const techMilvus = cdnImage('generative-ai-development/Milvus.webp');
const techQdrant = cdnImage('ai-development-new/Qdrant.webp');
const techChroma = cdnImage('generative-ai-development/Chroma.webp');
const techElastic = cdnImage('Tech-stack-icons/elasticsearch.webp');
const techRest = cdnImage('ai-copliot/REST.webp');
const techGraphQL = cdnImage('ai-copliot/GraphQL.webp');
const techKafka = cdnImage('generative-ai-integration/Apache-Kafka.webp');
const techMuleSoft = cdnImage('Enterprise-Software/MuleSoft.webp');
const techSalesforce = cdnImage('Hire-SaaS-Developers/Salesforce-APIs.webp');
const techSAP = cdnImage('defi/Sap-cloud.webp');
const techSnowflake = cdnImage('ai-engineer-tech-stack/Snowflake.svg');
const techDocker = cdnImage('generative-ai-integration/Docker.webp');
const techKubernetes = cdnImage('generative-ai-integration/Kubernetes.webp');
const techTerraform = cdnImage('devops/Terraform.webp');
const techGitHubActions = cdnImage('Chatgpt-integration-services/GitHub-Actions.webp');
const techGitLab = cdnImage('generative-ai-integration/GitLab.webp');
const techJenkins = cdnImage('generative-ai-integration/Jenkins.webp');
const techLambda = cdnImage('generative-ai-integration/AWS-Lambda.webp');
const techCloudRun = cdnImage('generative-ai-integration/Google-Cloud-Run.webp');
const techTriton = cdnImage('Enterprise-AI-Development/NVIDIA-Triton-Inference-Server.webp');
const techLangSmith = cdnImage('ai-chatbot-development/LangSmith-Hub.webp');
const techOtel = cdnImage('Hire-Node-js-Developers/OpenTelemetry.webp');
const techWandb = cdnImage('generative-ai-consulting-company/Weights-Biases.webp');
const techGrafana = cdnImage('Tech-stack-icons/grafana.webp');
const techLangGraphIcon = genAiAsset('langgraph.svg');
const techGoogleAdk = genAiAsset('google-adk.svg');
const techPydanticAi = genAiAsset('pydantic-ai.svg');
const techBedrockAgents = genAiAsset('amazon-bedrock-agents.svg');
const techHaystack = genAiAsset('haystack.svg');
const techDspy = genAiAsset('dspy.svg');
const techLiteLlm = genAiAsset('litellm.svg');
const techOpenRouter = genAiAsset('openrouter.svg');
const techAzureAiFoundry = genAiAsset('microsoft-azure-ai-foundry.svg');
const techVertexAi = genAiAsset('google-vertex-ai.svg');
const techOpenAiEnterprise = genAiAsset('openai-enterprise.svg');
const techMosaicAi = genAiAsset('databricks-mosaic-ai.svg');
const techPgVectorIcon = genAiAsset('pgvector.svg');
const techAzureAiSearch = genAiAsset('azure-ai-search.svg');
const techServiceNowIcon = genAiAsset('servicenow.svg');
const techDynamics365 = genAiAsset('microsoft-dynamics-365.svg');
const techArizePhoenix = genAiAsset('arize-phoenix.svg');
const techLangfuse = genAiAsset('langfuse.svg');
const techMlflowIcon = genAiAsset('mlflow.svg');
const techOpenAiEvals = genAiAsset('openai-evals.svg');
const techRagas = genAiAsset('ragas.svg');
const techPrometheus = cdnImage('generative-ai-consulting-company/Prometheus.webp');

export const genAiTechEcosystemData = [
    {
        heading: 'Foundation Models',
        description:
            'Commercial and open-source models selected for accuracy, privacy, latency, context requirements, and deployment flexibility.',
        values: [
            {icon: techOpenAI, label: 'OpenAI GPT'},
            {icon: techClaude, label: 'Anthropic Claude'},
            {icon: techGemini, label: 'Google Gemini'},
            {icon: techLlama, label: 'Meta Llama'},
            {icon: techMistral, label: 'Mistral AI'},
            {icon: techPhi, label: 'Microsoft Phi'},
            {icon: techCohere, label: 'Cohere'},
            {icon: techHuggingFace, label: 'Hugging Face'}
        ]
    },
    {
        heading: 'AI Agent Frameworks',
        description: 'Frameworks for building governed AI agents, multi-step workflows, tool use, memory, and enterprise automation.',
        values: [
            {icon: techOpenAI, label: 'OpenAI Agents SDK'},
            {icon: techLangGraphIcon, label: 'LangGraph'},
            {icon: techCrewAI, label: 'CrewAI'},
            {icon: techAutoGen, label: 'Microsoft AutoGen'},
            {icon: techGoogleAdk, label: 'Google ADK'},
            {icon: techPydanticAi, label: 'Pydantic AI'},
            {icon: techBedrockAgents, label: 'Amazon Bedrock Agents'}
        ]
    },
    {
        heading: 'LLM Orchestration Frameworks',
        description: 'Frameworks for prompt orchestration, model routing, retrieval workflows, and enterprise AI execution.',
        values: [
            {icon: techLangChain, label: 'LangChain'},
            {icon: techLlamaIndex, label: 'LlamaIndex'},
            {icon: techSemanticKernel, label: 'Semantic Kernel'},
            {icon: techHaystack, label: 'Haystack'},
            {icon: techDspy, label: 'DSPy'},
            {icon: techLiteLlm, label: 'LiteLLM'},
            {icon: techLlamaIndex, label: 'Instructor'},
            {icon: techOpenRouter, label: 'OpenRouter'}
        ]
    },
    {
        heading: 'Cloud AI Platforms',
        description: 'Managed and private AI platforms for secure deployment, governance, and enterprise-scale AI infrastructure.',
        values: [
            {icon: techAzureAiFoundry, label: 'Azure AI Foundry'},
            {icon: techBedrock, label: 'Amazon Bedrock'},
            {icon: techVertexAi, label: 'Google Vertex AI'},
            {icon: techOpenAiEnterprise, label: 'OpenAI Enterprise'},
            {icon: techMosaicAi, label: 'Databricks Mosaic AI'},
            {icon: techNvidia, label: 'NVIDIA AI Enterprise'}
        ]
    },
    {
        heading: 'Vector Databases & Retrieval',
        description:
            'Enterprise retrieval platforms for semantic search, hybrid search, metadata filtering, and scalable RAG implementations.',
        values: [
            {icon: techPinecone, label: 'Pinecone'},
            {icon: techWeaviate, label: 'Weaviate'},
            {icon: techMilvus, label: 'Milvus'},
            {icon: techPgVectorIcon, label: 'pgvector'},
            {icon: techQdrant, label: 'Qdrant'},
            {icon: techChroma, label: 'Chroma'},
            {icon: techElastic, label: 'Elasticsearch'},
            {icon: techAzureAiSearch, label: 'Azure AI Search'}
        ]
    },
    {
        heading: 'Enterprise Data & Integration',
        description: 'Enterprise applications, APIs, databases, and integration services that connect AI with business operations.',
        values: [
            {icon: techRest, label: 'REST APIs'},
            {icon: techGraphQL, label: 'GraphQL'},
            {icon: techKafka, label: 'Apache Kafka'},
            {icon: techMuleSoft, label: 'MuleSoft'},
            {icon: techSalesforce, label: 'Salesforce'},
            {icon: techSAP, label: 'SAP'},
            {icon: techServiceNowIcon, label: 'ServiceNow'},
            {icon: techSnowflake, label: 'Snowflake'},
            {icon: techDatabricks, label: 'Databricks'},
            {icon: techDynamics365, label: 'Microsoft Dynamics 365'}
        ]
    },
    {
        heading: 'Infrastructure & Deployment',
        description: 'Production infrastructure for deployment, scaling, security, and continuous delivery.',
        values: [
            {icon: techDocker, label: 'Docker'},
            {icon: techKubernetes, label: 'Kubernetes'},
            {icon: techTerraform, label: 'Terraform'},
            {icon: techGitHubActions, label: 'GitHub Actions'},
            {icon: techGitLab, label: 'GitLab CI/CD'},
            {icon: techJenkins, label: 'Jenkins'},
            {icon: techLambda, label: 'AWS Lambda'},
            {icon: techAzure, label: 'Azure Functions'},
            {icon: techCloudRun, label: 'Google Cloud Run'},
            {icon: techTriton, label: 'NVIDIA Triton'}
        ]
    },
    {
        heading: 'Evaluation, LLMOps & Monitoring',
        description: 'Tools for evaluating model quality, monitoring production performance, tracing AI workflows, and optimizing costs.',
        values: [
            {icon: techLangSmith, label: 'LangSmith'},
            {icon: techArizePhoenix, label: 'Arize Phoenix'},
            {icon: techLangfuse, label: 'Langfuse'},
            {icon: techMlflowIcon, label: 'MLflow'},
            {icon: techOtel, label: 'OpenTelemetry'},
            {icon: techWandb, label: 'Weights & Biases'},
            {icon: techGrafana, label: 'Grafana'},
            {icon: techPrometheus, label: 'Prometheus'},
            {icon: techOpenAiEvals, label: 'OpenAI Evals'},
            {icon: techRagas, label: 'Ragas'}
        ]
    }
];

// ─── Business value (CoreServices) ───────────────────────────────────────────

// CoreServices renders a non-StaticImageData `icon` as a React node, so these are
// MUI icons rather than images — the navy tile paints them white automatically.
export const genAiBusinessValueData = [
    {
        icon: valKnowledgeAccess,
        title: 'Faster Knowledge Access',
        redirect: '/ai-copilot-development',
        description:
            'Help employees find accurate information across approved documents, systems, and databases without switching between multiple tools.'
    },
    {
        icon: valManualWork,
        title: 'Reduced Manual Work',
        redirect: '/blog/ai-workflow-automation-for-enterprises',
        description: 'Automate repetitive drafting, summarization, classification, extraction, routing, and information-transfer tasks.'
    },
    {
        icon: valCustomerResponse,
        title: 'Improved Customer Response',
        redirect: '/ai-chatbot-development-company',
        description:
            'Provide service teams with context-aware assistance connected to customer history, product data, policies, and escalation workflows.'
    },
    {
        icon: valConsistentOps,
        title: 'More Consistent Operations',
        redirect: '/enterprise-software-development',
        description: 'Apply common knowledge, rules, templates, and approval steps across high-volume business processes.'
    },
    {
        icon: valProductivity,
        title: 'Stronger Employee Productivity',
        redirect: '/ai-copilot-development',
        description: 'Embed copilots into existing applications so teams can complete knowledge-intensive tasks with less friction.'
    },
    {
        icon: valControlledAdoption,
        title: 'Controlled AI Adoption',
        redirect: '/ai-deployment-services',
        description:
            'Replace unmanaged AI usage with governed systems that provide access control, monitoring, traceability, and cost visibility.'
    },
    {
        icon: valInnovation,
        title: 'Faster Product Innovation',
        redirect: '/generative-ai-development-company',
        description:
            'Add intelligent search, assistants, content generation, workflow automation, and agent capabilities to existing digital products.'
    },
    {
        icon: valScalableOps,
        title: 'Scalable AI Operations',
        redirect: '/mlops-consulting-services',
        description:
            'Move from isolated pilots to a maintainable production architecture that supports growing users, data, integrations, and use cases.'
    }
];

// ─── Why Debut Infotech (WhatSetsBest) ───────────────────────────────────────

export const genAiWhatSetsBestData = {
    heading: 'Why Choose Debut Infotech As Your Enterprise Generative AI Integration Partner?',
    body: 'Enterprise AI succeeds when strategy, architecture, engineering, data, and governance work together. Debut Infotech helps organizations design, integrate, and scale production-ready <a href="/generative-ai-development-company" target="_blank">generative AI solutions</a> that fit existing systems, workflows, and long-term business goals.',
    features: [
        {
            icon: whyBusinessFirst,
            iconWidth: 28,
            iconHeight: 28,
            title: 'Business-First Integration Strategy',
            description:
                'We align every engagement with your business objectives, workflow challenges, and success metrics, ensuring AI is implemented where it delivers measurable value.'
        },
        {
            icon: whyEnterpriseScale,
            iconWidth: 28,
            iconHeight: 28,
            title: 'Enterprise-Scale Engineering Experience',
            description:
                'With 15+ years of software engineering expertise and 500+ technology solutions delivered, we build enterprise systems designed for reliability and scale.'
        },
        {
            icon: whyEndToEnd,
            iconWidth: 28,
            iconHeight: 28,
            title: 'End-to-End Generative AI Integration Services',
            description:
                'From AI strategy and architecture to <a href="/blog/ai-knowledge-systems-use-rag-and-vector-databases" target="_blank">RAG implementation</a>, AI agents, deployment, governance, and optimization, we manage the complete delivery lifecycle.'
        },
        {
            icon: whySeamlessIntegration,
            iconWidth: 28,
            iconHeight: 28,
            title: 'Seamless Integration Across Enterprise Systems',
            description:
                'We securely <a href="/blog/integrate-generative-ai-into-enterprise-crm" target="_blank">integrate generative AI into enterprise</a> CRM, ERP, CMS, databases, SaaS platforms, APIs, collaboration tools, and legacy systems.'
        }
    ],
    deliverableEyebrow: 'EXPLORE DEBUT INFOTECH | END-TO-END DELIVERY CAPABILITIES',
    deliverableHeading: 'Full-Scope Enterprise Generative AI Integration Deliverables',
    deliverables: [
        'Enterprise AI strategy and solution architecture',
        'Business workflow discovery and AI use case assessment',
        'LLM, SLM, and multi-model integration architecture',
        'Enterprise application integration (CRM, ERP, CMS, APIs)',
        'Retrieval-Augmented Generation (RAG) pipeline implementation',
        'AI agent and workflow orchestration',
        'Enterprise knowledge base integration',
        'Prompt engineering and model behavior optimization',
        'Comprehensive testing, validation, and quality evaluation',
        'Secure deployment with monitoring and observability',
        'Role-based access control, governance, and audit logging',
        'Data privacy, compliance, and security guardrails',
        'Scalable cloud, hybrid, or on-premises deployment architecture',
        'Performance optimization, LLMOps, and continuous improvement'
    ]
};

// ─── Long-term optimization (HomeServices) ───────────────────────────────────

// Card order and wording follow the Figma for this section.
export const genAiOptimizationData = [
    {
        src: optResponseQuality,
        heading: 'Response Quality Monitoring',
        body: 'Evaluate accuracy, groundedness, completeness, consistency, and user feedback against representative business scenarios.'
    },
    {
        src: optModelEvaluation,
        heading: 'Model Evaluation and Upgrades',
        body: 'Assess newer models against existing benchmarks before changing production behavior, cost, privacy, or latency.'
    },
    {
        src: optCostOptimization,
        heading: 'Token and API Cost Optimization',
        body: 'Reduce unnecessary context, improve prompts, apply caching, route requests to suitable models, and monitor cost by workflow.'
    },
    {
        src: optRetrievalPerformance,
        heading: 'Retrieval Performance Improvement',
        body: 'Refine chunking, metadata, indexing, filters, reranking, and content freshness to improve enterprise knowledge retrieval.'
    },
    {
        src: optPromptVersioning,
        heading: 'Prompt and Workflow Versioning',
        body: 'Test controlled changes, track versions, compare results, and support rollback when updates affect quality or operations.'
    },
    {
        src: optSecurityReviews,
        heading: 'Security & Governance Reviews',
        body: 'Review access, logs, policies, new tools, changed data sources, model behavior, and compliance requirements at defined intervals.'
    }
];

// ─── FAQs ────────────────────────────────────────────────────────────────────

export const genAiFAQData = [
    {
        category: 'Generative AI Integration',
        questions: [
            {
                q: 'How can generative AI be integrated into existing enterprise software?',
                a: "The integration of generative AI into existing enterprise software can use APIs, middleware, webhooks, event streams, embedded interfaces, retrieval pipelines, and agent tools. The best method depends on the application's architecture, data access, workflow, security requirements, and performance needs."
            },
            {
                q: 'Can generative AI be connected to Salesforce CRM and SAP ERP?',
                a: 'Yes. AI assistants and workflows can be connected to Salesforce, SAP, and other enterprise systems through supported APIs, middleware, integration platforms, or custom services. Access permissions, data scope, business rules, and audit requirements should be defined before implementation.'
            },
            {
                q: 'What is the difference between LLM fine-tuning and RAG pipeline integration?',
                a: "<a href='/llm-fine-tuning-services' target='_blank'>LLM fine-tuning</a> changes a model's behavior by training it on selected examples. RAG keeps enterprise knowledge outside the model and retrieves relevant information at request time. RAG is generally better for frequently changing or permission-controlled information, while fine-tuning can help with specialized behavior, format, tone, or task patterns. Some solutions use both."
            },
            {
                q: 'How do you prevent corporate data leakage when using public LLM APIs?',
                a: 'Secure generative AI integration for regulated industries may include data minimization, sensitive-data masking, provider configuration, private endpoints, encryption, access policies, approved data boundaries, prompt filtering, audit logs, and contractual review. Highly sensitive workflows may require private, cloud-isolated, or on-premises model deployment.'
            },
            {
                q: 'Can a private LLM be deployed on premises?',
                a: 'Yes. Suitable open-source or privately hosted models can be deployed on premises, in a virtual private cloud, or in a hybrid environment. The decision depends on data sensitivity, infrastructure, model requirements, latency, scale, and total cost of ownership.'
            },
            {
                q: 'Can generative AI be integrated with legacy systems without causing downtime?',
                a: "In many cases, yes. We use APIs, middleware, adapters, data services, phased releases, feature flags, and parallel validation to reduce disruption. The exact approach depends on the legacy system's interfaces, data access, and operational constraints."
            },
            {
                q: 'How long does it take to integrate an LLM into a corporate technology stack?',
                a: 'A focused integration or pilot may take several weeks, while a multi-system enterprise rollout can take several months. Timeline depends on use-case complexity, data readiness, number of integrations, security review, compliance, testing, and production scale.'
            },
            {
                q: 'What is the typical ROI of enterprise generative AI integration services?',
                a: 'ROI depends on the workflow and adoption level. Common value areas include reduced handling time, faster knowledge access, fewer manual steps, improved employee productivity, more consistent customer service, and faster product delivery. We recommend defining baseline metrics before implementation and measuring results during the pilot.'
            },
            {
                q: 'How do you optimize token usage and API costs after launch?',
                a: 'We improve prompts, limit unnecessary context, optimize retrieval, cache repeated information, route tasks to suitable models, use smaller models where appropriate, compress conversation history, and monitor cost by user, feature, and workflow.'
            },
            {
                q: 'What are the common engagement models for hiring a generative AI integration company?',
                a: "Common models include consulting and discovery, fixed-scope pilot delivery, time-and-materials development, <a href='/hire-generative-ai-developers' target='_blank'>dedicated AI engineering teams</a>, team extension, and managed product delivery. The right model depends on scope clarity, internal capability, timeline, and long-term ownership."
            },
            {
                q: 'How much does enterprise generative AI integration cost?',
                a: 'Cost depends on the number of systems, data quality, RAG requirements, agent complexity, model choice, private deployment, security controls, user volume, and ongoing support. A discovery and architecture assessment is the most reliable way to define a realistic range.'
            },
            {
                q: 'How do you integrate generative AI into enterprise workflows?',
                a: 'Enterprise generative AI integration starts by identifying high-value business use cases and connecting AI models to enterprise applications, data sources, and knowledge repositories. The solution is then integrated into existing workflows using APIs, RAG, AI agents, or orchestration tools, with security, governance, and monitoring built in for production use.'
            },
            {
                q: 'What is the main ROI of integrating generative AI?',
                a: 'The primary ROI comes from improving productivity, reducing manual effort, and accelerating business processes. By automating repetitive tasks, enabling faster access to enterprise knowledge, and supporting better decision-making, organizations can lower operational costs while improving employee and customer experiences.'
            },
            {
                q: 'Which steps are part of the enterprise generative AI integration process?',
                a: 'Enterprise generative AI integration typically follows a structured implementation process: 1) Define business objectives and identify high-value AI use cases. 2) Assess data and system readiness to evaluate existing applications, data quality, and integration requirements. 3) Design the AI architecture by selecting the appropriate models, RAG strategy, and deployment approach. 4) Integrate AI with enterprise systems such as CRM, ERP, databases, and knowledge repositories. 5) Implement security and governance through access controls, compliance measures, and monitoring. 6) Test and optimize the solution before production deployment. 7) Monitor, improve, and scale the AI system based on performance, user feedback, and evolving business needs.'
            }
        ]
    }
];
